import { Link, useNavigate } from "react-router-dom";
import { LogIn, User, Sparkles, Blocks, LayoutDashboard, LogOut, Gift, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useConversations } from "@/hooks/useMessages";

export const NavbarDesktop = () => {
  const { user, profile, openAuthModal, signOut } = useAuth();
  const navigate = useNavigate();
  const { data: conversations = [] } = useConversations();

  const totalUnreadMessages = user
    ? conversations.reduce((acc, c) => acc + (c.unreadCount || 0), 0)
    : 0;

  const displayName = profile?.full_name || user?.email?.split("@")[0] || "Usuario";
  const walletBalanceCents = profile?.wallet_balance_cents || 0;
  const walletBalanceEur = (walletBalanceCents / 100).toFixed(2).replace(".", ",");

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <header data-web-navbar="true" className="sticky top-0 z-40 bg-[#FAF8F5]/90 dark:bg-background/90 backdrop-blur-md border-b border-stone-200/70 dark:border-border transition-colors">
      <div className="container mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="p-2 rounded-xl bg-primary text-primary-foreground transition-transform group-hover:scale-105">
              <Blocks className="h-6 w-6" />
            </div>
            <span className="font-display font-extrabold text-2xl tracking-tight text-stone-900 dark:text-foreground">
              brickshare<span className="text-primary">.</span>
            </span>
          </Link>
        </div>

        <nav className="hidden md:flex items-center gap-8 text-[1.1rem] font-semibold text-stone-700 dark:text-muted-foreground">
          <Link to="/" className="hover:text-stone-900 dark:hover:text-foreground transition-colors">
            Alquila
          </Link>
          <Link to="/catalogo" className="hover:text-stone-900 dark:hover:text-foreground transition-colors">
            Explora
          </Link>
          <Link to="/sobre-nosotros" className="hover:text-stone-900 dark:hover:text-foreground transition-colors">
            Sobre nosotros
          </Link>
          <Link to="/preguntas-frecuentes" className="hover:text-stone-900 dark:hover:text-foreground transition-colors">
            FAQs
          </Link>
          <Link
            to="/regalar"
            className="hover:text-stone-900 dark:hover:text-foreground transition-colors"
          >
            Regala
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="relative inline-flex items-center">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full border-stone-300 dark:border-border font-medium cursor-pointer"
                  >
                    <User className="h-4 w-4 mr-2" />
                    {displayName}
                    {walletBalanceCents > 0 && (
                      <span className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 text-xs font-bold">
                        {walletBalanceEur} €
                      </span>
                    )}
                  </Button>
                  {totalUnreadMessages > 0 && (
                    <span
                      data-testid="unread-messages-badge"
                      className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold shadow-sm pointer-events-none animate-in zoom-in-50 duration-200"
                    >
                      {totalUnreadMessages > 99 ? "99+" : totalUnreadMessages}
                    </span>
                  )}
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 p-1.5 shadow-lg rounded-xl">
                <div className="px-3 py-2 text-xs text-muted-foreground border-b border-border/50 mb-1">
                  Conectado como <strong className="font-semibold text-foreground block truncate">{displayName}</strong>
                  {walletBalanceCents > 0 && (
                    <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 block mt-0.5">
                      Saldo disponible: {walletBalanceEur} €
                    </span>
                  )}
                </div>
                <DropdownMenuItem
                  className="cursor-pointer gap-2 py-2 px-3 rounded-lg"
                  onClick={() => navigate("/mensajes")}
                >
                  <MessageSquare className="h-4 w-4 text-primary" />
                  <span className="flex-1">Mensajes</span>
                  {totalUnreadMessages > 0 && (
                    <span className="ml-auto px-1.5 py-0.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold">
                      {totalUnreadMessages > 99 ? "99+" : totalUnreadMessages}
                    </span>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer gap-2 py-2 px-3 rounded-lg"
                  onClick={() => navigate("/dashboard")}
                >
                  <LayoutDashboard className="h-4 w-4 text-primary" />
                  <span>Panel de usuario</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer gap-2 py-2 px-3 rounded-lg"
                  onClick={() => navigate("/regalar")}
                >
                  <Gift className="h-4 w-4 text-amber-500" />
                  <span>Regalar un bono</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="my-1" />
                <DropdownMenuItem
                  className="cursor-pointer gap-2 py-2 px-3 rounded-lg text-destructive focus:text-destructive focus:bg-destructive/10"
                  onClick={handleSignOut}
                >
                  <LogOut className="h-4 w-4" />
                  <span>Salir</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="rounded-full text-stone-700 dark:text-muted-foreground hover:text-stone-900 dark:hover:text-foreground font-medium"
                onClick={() => openAuthModal("login")}
              >
                <LogIn className="h-4 w-4 mr-1.5" />
                Iniciar sesión
              </Button>
              <Button
                size="sm"
                data-testid="register-link"
                className="rounded-full bg-stone-900 hover:bg-stone-800 text-white font-medium px-5 shadow-sm"
                onClick={() => openAuthModal("signup")}
              >
                Registrarme gratis
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default NavbarDesktop;
